import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";
import StatusChip from "@/components/StatusChip";
import { formatAmount, formatDate } from "@/lib/format";
import { CheckCircle2 } from "lucide-react";

// HISTORICAL, READ-ONLY. Bills handed to Expense Capture before 1OS Bills became the
// canonical supplier-Bill authority. This is provenance, not a current workflow state:
// nothing here can be actioned, and no new record can ever land here (the EC handoff is retired).
export default function SentToEcView() {
  const [rows, setRows] = useState([]);
  const [bills, setBills] = useState([]);

  useEffect(() => {
    (async () => {
      const [cands, b] = await Promise.all([
        api.listCandidates({ status: "SENT_TO_EC" }),
        api.listBills(),
      ]);
      setRows(cands);
      setBills(b);
    })();
  }, []);

  const billById = new Map(bills.map((b) => [b.id, b]));

  return (
    <div className="space-y-6" data-testid="legacy-ec-history">
      <header>
        <h1 className="text-[28px] font-semibold text-archival-primary tracking-tight leading-tight">
          Legacy · Sent to Expense Capture
        </h1>
        <p className="text-[14px] text-archival-secondary mt-1">
          Historical record of bills handed to Expense Capture before 1OS Bills became the canonical
          supplier-Bill authority. Read-only — these are not confirmed 1OS Bills, and no new record can
          land here.
        </p>
      </header>

      <div className="rounded-md border border-archival bg-archival-surface overflow-hidden">
        {rows.length === 0 ? (
          <div className="py-16 text-center text-archival-muted text-[13px]">
            No historical Expense Capture handoffs.
          </div>
        ) : (
          <ul>
            {rows.map((c) => {
              const draftId = c.ec_draft_bill_id || c.confirmed_bill_id;
              const b = draftId ? billById.get(draftId) : null;
              return (
                <li
                  key={c.id}
                  data-testid={TID.sentToEcRow(c.id)}
                  className="grid grid-cols-12 items-center gap-3 px-4 h-[72px] border-b border-archival last:border-b-0 hover:bg-archival-soft"
                >
                  <div className="col-span-3 min-w-0">
                    <div className="text-[14px] font-semibold text-archival-primary truncate">
                      {b?.vendor_name_snapshot || c.suggested_vendor_name || "—"}
                    </div>
                    <div className="text-[11px] text-archival-muted truncate">{c.original_filename}</div>
                  </div>
                  <div className="col-span-2 text-[13px] font-mono text-archival-primary">
                    {b?.bill_number || c.bill_number || "—"}
                  </div>
                  <div className="col-span-2 text-[13px] font-mono text-archival-secondary">
                    {formatDate(b?.bill_date || c.bill_date)}
                  </div>
                  <div className="col-span-2 text-[14px] font-mono font-semibold text-archival-primary">
                    {formatAmount(b?.total_amount ?? c.total_amount, c.currency)}
                  </div>
                  <div className="col-span-2">
                    <StatusChip status="SENT_TO_EC" />
                  </div>
                  <div className="col-span-1 text-right">
                    {c.ec_bill_url ? (
                      <a
                        href={c.ec_bill_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[12px] text-archival-secondary hover:text-archival-primary underline-offset-4 hover:underline inline-flex items-center gap-1"
                      >
                        <CheckCircle2 size={11} /> Open historical EC record
                      </a>
                    ) : (
                      <Link
                        to={`/candidates/${c.id}`}
                        className="text-[12px] text-archival-secondary hover:text-archival-primary underline-offset-4 hover:underline inline-flex items-center gap-1"
                      >
                        <CheckCircle2 size={11} /> View
                      </Link>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
